import clsx from 'clsx';
import _ from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { BILLING_MODE_MONTH, PRODUCT_FREE_PRICE, UNKNOWN_DATA } from '@/config/appConstants';
import {
  BillingMode,
  Currency,
  ProductFeaturesTypes,
  ProductTypes,
  UserProductTypes,
} from '@/types';
import {
  getProductDetails,
  isPrepaidProduct,
  getLicensedPrice,
  getLicensedPriceByInterval,
  getIntervalCountByBillingMode,
  getCurrencySymbol,
  getPriceText,
  getPrepaidProductPriceByCurrency,
  getBillingModePriceText,
} from '@/utils/helpers';

export type UpdateProductCardProps = {
  key?: string;
  userProduct?: UserProductTypes;
  product?: ProductTypes;
  currency: Currency;
  billingMode?: BillingMode;
  className?: string;
  formatProductName?: (productName: string | ProductFeaturesTypes) => string;
  formatCredits?: (product: ProductTypes) => React.ReactNode | string;
  formatCategoryName?: (productName: string | ProductFeaturesTypes) => string;
  isANewProductToUpdate: boolean;
};

export const UpdateProductCard = ({
  userProduct,
  product,
  currency,
  className: cardClass,
  formatProductName = undefined,
  formatCredits = undefined,
  formatCategoryName = undefined,
  billingMode = BILLING_MODE_MONTH,
  isANewProductToUpdate,
}: UpdateProductCardProps) => {
  const { t, i18n } = useTranslation();
  product || (product = userProduct?.product);

  const isPrepaid = product ? isPrepaidProduct(product) : false;

  if (!product) {
    return (
      <article className={cardClass ? cardClass : 'p-1 w-full md:w-1/2 xl:w-1/4 pt-8 rounded-lg'}>
        <p className="font-black text-xl uppercase tracking-tighter text-md-black text-center mt-20">
          {t('ProductDetail.productNotAvailable')}
        </p>
      </article>
    );
  }

  const getProductNameTranslated = (productName: string | ProductFeaturesTypes) => {
    return t(`ProductDetail.${productName}`) === `ProductDetail.${productName}`
      ? productName
      : t(`ProductDetail.${productName}`);
  };

  const productName = getProductDetails(product, 'productName');
  const categoryName = getProductDetails(product, 'categoryName') as string;
  const categoryNameParsed = _.camelCase(categoryName.replace(/\s/g, ''));
  const categoryNameTranslated = t(`ProductDetail.${categoryNameParsed}`);
  const productNameTranslated = getProductNameTranslated(productName);

  const getProductPriceByBillingMode = () => {
    const intervalCount = getIntervalCountByBillingMode(billingMode);

    if (!intervalCount && product) {
      const price = getPrepaidProductPriceByCurrency(product.prices, currency);
      return getPriceText(price, currency, i18n.language);
    }

    if (isANewProductToUpdate) {
      return product
        ? getLicensedPriceByInterval(product.prices, intervalCount, currency).amount
        : UNKNOWN_DATA;
    }

    if (!userProduct?.groupProduct) {
      return UNKNOWN_DATA;
    }

    if (userProduct?.groupProduct.pricing) {
      return getLicensedPrice(userProduct.groupProduct.pricing).amount;
    }

    return `${PRODUCT_FREE_PRICE} ${getCurrencySymbol(currency)}`;
  };

  const productPrice = getProductPriceByBillingMode();
  const priceText = getPriceText(productPrice, currency, i18n.language);

  return (
    <article
      className={clsx(cardClass, {
        'product__card product__card--default': !isANewProductToUpdate && !isPrepaid,
        'product__card product__card--new': isANewProductToUpdate || isPrepaid,
      })}
    >
      <p className="card__category__name">
        {formatCategoryName
          ? formatCategoryName(categoryNameTranslated)
          : isPrepaid
          ? categoryNameTranslated
          : categoryName}
      </p>
      {!formatCredits && (
        <p className="card__category__badge">
          {formatProductName ? formatProductName(productName) : productNameTranslated}
        </p>
      )}
      <div className="card__price">{priceText}</div>
      <p className="card__price--mode">{t(getBillingModePriceText(billingMode))}</p>
    </article>
  );
};
